import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Array, AsyncStateN } from '@blade/std-lib'
import { Typography } from '@blade/ui-kit'
import { walletsModelAtom } from '@root/entities/wallet/atoms'
import {
  WalletListItem,
  WalletListItemSkeleton,
} from '@root/entities/wallet/ui'
import { useAppRoutes } from '@root/features/routing'
import { useTranslation } from '@root/shared/localization'
import { FadeOverflow, InfoMessage } from '@root/shared/ui-kit'

import {
  showOnlyFavoritesAtom,
  walletsExpandedAtom,
  walletsToShowStateAtom,
} from '../atoms'

import { WalletsListEmpty } from './wallets-list-empty'

import type { WalletAssetModel } from '@root/entities/wallet/atoms'
import type { WalletModel } from '@root/entities/wallet/atoms/reatom-wallet'

const placeholders = Array.range(1, 5)

type WalletsListProps = {
  onOperations: (wallet: WalletModel) => void
  onAssetClick: (asset: WalletAssetModel) => void
}

export const WalletsList: React.FC<WalletsListProps> = ({
  onOperations,
  onAssetClick,
}) => {
  const { t } = useTranslation()
  const { navigateToWalletManagerWallet } = useAppRoutes()

  const [walletsState] = useAtom(walletsToShowStateAtom)
  const [onlyFavorites] = useAtom(showOnlyFavoritesAtom)
  const [walletsExpanded] = useAtom(walletsExpandedAtom)

  const [model] = useAtom(walletsModelAtom)
  const loadWallets = useAction(model.load)

  const renderContent = () => {
    if (AsyncStateN.isSuccess(walletsState)) {
      const wallets = walletsState.value

      if (wallets.length === 0) {
        if (onlyFavorites) {
          return (
            <EmptyContainer>
              {t('wallet.no_favorite_wallets_message')}
            </EmptyContainer>
          )
        }

        return <WalletsListEmpty />
      }

      return (
        <FadeOverflow mode="always" variant="vertical" blockEnd={60}>
          <List>
            {wallets.map((wallet, idx) => {
              return (
                <WalletListItem
                  key={idx}
                  wallet={wallet}
                  expanded={walletsExpanded}
                  variant="default"
                  active={false}
                  onClick={() => {
                    navigateToWalletManagerWallet(wallet.id)
                  }}
                  onDetailsClick={(e) => {
                    e.stopPropagation()
                    onOperations(wallet)
                  }}
                  onAssetClick={onAssetClick}
                />
              )
            })}
          </List>
        </FadeOverflow>
      )
    }

    if (AsyncStateN.isError(walletsState)) {
      return <InfoMessage intent="error" onRetry={loadWallets} />
    }

    return (
      <List>
        {placeholders.map((it) => (
          <WalletListItemSkeleton key={it} />
        ))}
      </List>
    )
  }

  return <Root data-ignore-ptr>{renderContent()}</Root>
}

const Root = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr);
`

const List = styled.div`
  display: grid;
  gap: 4px;
  padding-block: 10px 120px;
  padding-inline: 10px;

  align-items: start;
  align-content: start;
  height: 100%;
  overflow-y: auto;
`

const EmptyContainer = styled(Typography).attrs({
  variant: 'body4-regular',
  component: 'div',
})`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 64px;
  border-radius: 12px;
  margin-inline: 12px;
  margin-top: 16px;

  border: 1px solid rgba(255, 255, 255, 0.1);

  color: var(--text-12);
  text-align: center;
`
